/**
 * Cognito hosted-UI auth (Authorization Code + PKCE).
 *
 * The Cognito app client is public (no secret) and code-grant only, so login is
 * a full-page redirect to the hosted UI, and the returned ?code= is exchanged
 * for tokens at /oauth2/token with a PKCE verifier. No client secret ships to
 * the browser.
 *
 * Ported from the dashboard (flow-ai/dashboard
 * lib/client/app/shared/utils/cognito-auth.js) — same flow, this app's storage
 * helpers.
 */

import { cognitoConfig, assertCognitoConfigured } from 'shared/utils/auth-config'
import { setToken, clearTokenAndProfile } from 'shared/utils/token'
import Session from 'shared/utils/session'
import TabSession from 'shared/utils/tabSession'

const PKCE_VERIFIER_KEY = 'cognito_pkce_verifier'
const AUTH_STATE_KEY = 'cognito_auth_state'
const REFRESH_TOKEN_KEY = 'cognito_refresh_token'

const base64UrlEncode = bytes => {
  let str = ''
  bytes.forEach(b => { str += String.fromCharCode(b) })
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const randomVerifier = () => {
  const bytes = new Uint8Array(64)
  window.crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

const challengeFromVerifier = async verifier => {
  const data = new TextEncoder().encode(verifier)
  const digest = await window.crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(new Uint8Array(digest))
}

const authorizeBase = () => `https://${cognitoConfig.domain}/oauth2`

// Set once a sign-out has claimed the page, and never cleared: the hosted
// /logout trip ends in a fresh document where this module starts over.
//
// Signing out is a full-page navigation to Cognito's /logout, and that request
// completing is the ONLY thing that drops the hosted-UI session cookie. But
// signing out also flips the app to "not authenticated", and PrivateRoute
// answers that by starting a login — a second navigation, issued a microtask
// later, which aborts the first. Cognito never sees the /logout, the cookie
// survives, and /authorize hands back a fresh code against the still-live
// session: the user is silently signed back in, revoked refresh token and all.
let loggingOut = false

const currentPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`

const readStoredState = () => {
  const raw = TabSession.getItem(AUTH_STATE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (err) {
    return null
  }
}

/**
 * Kick off login: build a PKCE challenge, stash the verifier, and redirect the
 * browser to the Cognito hosted UI. The verifier goes in sessionStorage (tab
 * scoped) so a second tab's in-flight login cannot overwrite this one's.
 *
 * The state parameter is a random token stored alongside the verifier, with the
 * pre-login path kept next to it so a deep link survives the redirect — the
 * redirect_uri is a fixed build-time constant and carries no path of its own.
 */
export const beginLogin = async ({ identityProvider } = {}) => {
  // A sign-out already owns the page — see loggingOut below.
  if (loggingOut) return

  // A misbuilt bundle with no Cognito config must fail loudly here rather than
  // redirect to `https://null/oauth2/authorize`. This is the standalone login
  // path, so the Care-embedded path (which never calls beginLogin) is untouched.
  assertCognitoConfigured()

  const verifier = randomVerifier()
  const challenge = await challengeFromVerifier(verifier)
  const nonce = randomVerifier()
  TabSession.setItem(PKCE_VERIFIER_KEY, verifier)
  TabSession.setItem(AUTH_STATE_KEY, JSON.stringify({ nonce, path: currentPath() }))

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoConfig.clientId,
    redirect_uri: cognitoConfig.redirectUri,
    scope: cognitoConfig.scope,
    code_challenge_method: 'S256',
    code_challenge: challenge,
    state: nonce
  })
  if (identityProvider) params.set('identity_provider', identityProvider)

  // Checked again here, not only on entry: beginLogin is async, so a login that
  // started before the sign-out is still parked on the digest above and would
  // otherwise assign over the /logout navigation once it resumes.
  if (loggingOut) return

  window.location.assign(`${authorizeBase()}/authorize?${params.toString()}`)
}

const postToken = async body => {
  let res
  try {
    res = await fetch(`${authorizeBase()}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(body).toString()
    })
  } catch (networkErr) {
    // fetch rejects only when no HTTP response arrived at all (offline, DNS,
    // CORS). That is transient by nature — mark it so a refresh caller retries
    // rather than treating the session as dead.
    const err = new Error(`Cognito token endpoint unreachable: ${networkErr && networkErr.message}`)
    err.transient = true
    throw err
  }
  if (!res.ok) {
    const detail = await res.text()
    const err = new Error(`Cognito token endpoint returned ${res.status}: ${detail}`)
    err.status = res.status
    // A refresh token that is expired or revoked comes back as 400
    // invalid_grant — genuinely session-ending. A 5xx, a 429 throttle, or a
    // 408 timeout is transient and must NOT end the session.
    err.transient = res.status >= 500 || res.status === 429 || res.status === 408
    throw err
  }
  return res.json()
}

const persistTokens = tokens => {
  if (tokens.id_token) setToken(tokens.id_token)
  if (tokens.refresh_token) Session.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
  return tokens
}

/**
 * True when the current URL is a hosted-UI redirect carrying an auth code.
 */
export const hasAuthCode = () =>
  new URLSearchParams(window.location.search).has('code')

const NO_ACCESS_MESSAGE =
  "You don't have access to this platform. Please contact your administrator."

/**
 * Turn what Cognito puts on the URL into something worth showing a person.
 *
 * The raw text is plumbing, not a message: a rejection from the pre-signup
 * Lambda arrives as "PreSignUp failed with error <thrown message>", because
 * Cognito wraps whatever the Lambda threw. Nobody outside this codebase should
 * be shown the name of a Lambda trigger, so the known cases are mapped and the
 * raw text is kept only for the console.
 */
const friendlyMessage = (error, description) => {
  const raw = `${error} ${description}`.toLowerCase()

  // The pool is invitation-only. Every refusal the pre-signup Lambda raises —
  // no matching account, unverified social email, a failed lookup failing
  // closed — means the same thing to the person in front of the screen.
  if (raw.includes('presignup') || raw.includes('invitation only')) {
    return NO_ACCESS_MESSAGE
  }

  // The hosted UI's own refusals, which never carry a useful description.
  if (error === 'access_denied') return NO_ACCESS_MESSAGE

  // Everything else gets the generic message. `error_description` arrives on
  // the URL, so anyone can craft a link that puts arbitrary text in it —
  // reflecting it on the login screen is a content-spoofing vector (React
  // escapes it, so no XSS, but "call this number to unlock your account" would
  // render verbatim). The raw text still reaches the console via getAuthError.
  return 'Something went wrong while signing you in. Please try again.'
}

/**
 * The hosted UI signals a rejected login by redirecting back to the callback
 * with ?error=&error_description= instead of ?code=. That is the normal shape
 * of an invite-only refusal on a federated sign-in, so it has to be read and
 * shown — a caller that only tests for `code` drops the user back on the login
 * screen with no explanation.
 *
 * Returns null when the URL carries no error. `message` is for the screen,
 * `description` is the raw text and belongs in the console.
 */
export const getAuthError = () => {
  const params = new URLSearchParams(window.location.search)
  const error = params.get('error')
  if (!error) return null

  // Cognito sends the description URL-encoded with + for spaces.
  const description = (params.get('error_description') || '').replace(/\+/g, ' ')

  return { error, description, message: friendlyMessage(error, description) }
}

/**
 * Drop the error query string so a reload doesn't resurrect a stale message.
 */
export const clearAuthError = () => {
  const params = new URLSearchParams(window.location.search)
  params.delete('error')
  params.delete('error_description')
  params.delete('state')

  const query = params.toString()
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  )
}

/**
 * Exchange the ?code= on the callback URL for tokens, then restore the URL the
 * user was originally after — which also strips the query string, so a refresh
 * doesn't replay a spent (single-use) code.
 */
export const completeLogin = async () => {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  if (!code) throw new Error('No authorization code on callback URL')

  const verifier = TabSession.getItem(PKCE_VERIFIER_KEY)
  if (!verifier) throw new Error('Missing PKCE verifier for code exchange')

  const storedState = readStoredState()
  if (!storedState) throw new Error('Missing stored state for code exchange')
  if (params.get('state') !== storedState.nonce) throw new Error('State mismatch on callback URL')

  const tokens = await postToken({
    grant_type: 'authorization_code',
    client_id: cognitoConfig.clientId,
    code,
    redirect_uri: cognitoConfig.redirectUri,
    code_verifier: verifier
  })

  TabSession.removeItem(PKCE_VERIFIER_KEY)
  TabSession.removeItem(AUTH_STATE_KEY)

  // The pre-login path is returned rather than applied here. Writing it with
  // window.history.replaceState would change the address bar without telling
  // React Router, which captured the callback URL when it mounted — the router
  // would still believe it is on the callback path and match the wrong route.
  // The caller navigates with the router's own history instead.
  return { ...persistTokens(tokens), path: storedState.path || window.location.pathname }
}

/**
 * Silent refresh using the stored refresh token. Rejects if no refresh token is
 * held.
 */
export const refreshTokens = async () => {
  const refreshToken = Session.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken) throw new Error('No Cognito refresh token available')

  const tokens = await postToken({
    grant_type: 'refresh_token',
    client_id: cognitoConfig.clientId,
    refresh_token: refreshToken
  })
  // A refresh_token grant does not return a new refresh_token; keep the old one.
  return persistTokens(tokens)
}

/**
 * Revoke the refresh token grant on Cognito's side. Fire-and-forget: the logout
 * redirect must not wait on it, and a failure here must not stop local cleanup.
 */
const revokeRefreshToken = () => {
  const refreshToken = Session.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken) return

  try {
    fetch(`${authorizeBase()}/revoke`, {
      method: 'POST',
      // keepalive lets the POST outlive the logout navigation that fires
      // immediately after; without it window.location.assign cancels the
      // in-flight request and the refresh token is never revoked server-side.
      keepalive: true,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        token: refreshToken,
        client_id: cognitoConfig.clientId
      }).toString()
    }).catch(() => {})
  } catch (err) {
    // ignore — local cleanup and the hosted /logout redirect still run
  }
}

/**
 * Clear local session and redirect through the hosted /logout so the Cognito
 * session cookie is dropped too.
 */
export const logout = () => {
  loggingOut = true

  revokeRefreshToken()

  clearTokenAndProfile()
  Session.removeItem(REFRESH_TOKEN_KEY)
  TabSession.removeItem(PKCE_VERIFIER_KEY)
  TabSession.removeItem(AUTH_STATE_KEY)

  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    logout_uri: cognitoConfig.logoutUri || cognitoConfig.redirectUri
  })
  window.location.assign(`https://${cognitoConfig.domain}/logout?${params.toString()}`)
}
